import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Accordion, AccordionDetails, AccordionSummary, Button, Typography } from '@mui/material';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { useState } from 'react';
import 'react-awesome-lightbox/build/style.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { theme } from 'thermia-react-container';
import { Content, GuideData, Item } from '../types/types';
// There are no types for react-awesome-lightbox
//@ts-ignore
import Lightbox from 'react-awesome-lightbox';

function GetIcon(guideType: string) {
  switch (guideType) {
    case 'ContentList':
      return <FormatListBulletedIcon />;
    case 'Content':
      return <ImportContactsIcon />;
    case 'Question':
      return <LiveHelpIcon />;
    case 'Answer':
      return <QuestionAnswerIcon />;
    default:
      return null;
  }
}

function ContentComponent({ content }: { content: Content }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<string>('');
  const handleImageClick = (imageUri: string) => {
    setCurrentImage(imageUri);
    setLightboxOpen(true);
  };

  return (
    <>
      {content.type === 'Html' && <ContentHtml html={content.html?.replace(/&nbsp;/g, " ")!} handleImageClick={handleImageClick} />}
      {content.type === 'Image' && <ContentImage url={content.url!} handleImageClick={handleImageClick} />}
      {content.type === 'Video' && <ContentVideo url={content.url!} />}
      {content.type === 'Guide' && <ContentGuide url={content.url!} />}

      {lightboxOpen && (
        <>
          <style>
            {
              /* this custom css is to hide the arrows in the lightbox */
              `
            .lb-icon-arrow {
              display: none !important;
            }
            `
            }
          </style>
          <Lightbox doubleClickZoom={0} images={[{ url: currentImage }]} onClose={() => setLightboxOpen(false)} />
        </>
      )}
    </>
  );
}

type ContentHtmlProps = { html: string; handleImageClick: (imageUrl: string) => void };

function ContentHtml({ html, handleImageClick }: ContentHtmlProps) {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element) {
        if (domNode.type === 'tag' && domNode.name === 'img' && !(domNode.parent instanceof Element && domNode.parent.name === 'a')) {
          const { attribs } = domNode;
          return (
            <div style={{ width: '100%', marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
              <img alt={domNode.attribs.alt} src={attribs.src} onClick={() => handleImageClick(attribs.src)} style={{ cursor: 'pointer' }} />
            </div>
          );
        }
      }
    }
  };

  return <div>{parse(html, options)}</div>;
}

type ContentImageProps = { url: string; handleImageClick: (imageUrl: string) => void };
function ContentImage({ url, handleImageClick }: ContentImageProps) {
  const { t } = useTranslation('troubleshoot');
  return (
    <div style={{ width: '100%', marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
      <img
        src={url}
        alt="img"
        title={t('troubleshoot.general.openImage')}
        onClick={() => {
          handleImageClick(url);
        }}
        style={{ cursor: 'pointer' }}
      />
    </div>
  );
}

function ContentVideo({ url }: { url: string }) {
  const { t } = useTranslation('troubleshoot');
  return (
    <div style={{ width: '100%', marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
      <Button variant="contained" style={{ marginTop: '20px' }} onClick={() => window.open(url, '_blank')}>
        {t('troubleshoot.general.openVideo')}
      </Button>
    </div>
  );
}

function ContentGuide({ url }: { url: string }) {
  const { t } = useTranslation('troubleshoot');
  const navigate = useNavigate();

  const navigateToGuide = () => {
    navigate(`/guide/${url}`);
  };

  return (
    <div style={{ width: '100%', marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
      <Button variant="contained" style={{ marginTop: '20px' }} onClick={() => navigateToGuide()}>
        {t('troubleshoot.general.open').toUpperCase()}
      </Button>
    </div>
  );
}

export function ItemComponent({ item }: { item: Item }) {
  const [expandedStates, setExpandedStates] = useState<boolean>(false);
  const isExpanded = expandedStates;
  const handleAccordionPress = () => {
    setExpandedStates(!expandedStates);
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <Accordion variant="outlined" sx={{ marginBottom: 4 }} title={item.heading} expanded={isExpanded} defaultExpanded={false}>
        <AccordionSummary
          sx={{ paddingHorizontal: 2, backgroundColor: theme.palette.backgroundExtraLight.main }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          onClick={() => handleAccordionPress()}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
            {GetIcon(item.itemType)}
            <Typography variant={'h6'}>{item.heading}</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          {item.items && item.items.map((childItem: Item, index: number) => <ItemComponent key={index} item={childItem} />)}
          {item.contents && item.contents.map((content: any, index: number) => <ContentComponent key={index} content={content} />)}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export default function GuideContent({ guide }: { guide: GuideData }) {
  const allContent = guide.items?.map((childItem: Item, index) => {
    return <ItemComponent key={index} item={childItem} />;
  });

  return <div>{allContent}</div>;
}
